/**
 * Underlying Compare strip data hook (internal name: options gutter):
 * per-strike book rows for ALL THREE underlyings (es, spx, spy) at once,
 * for one fill mode at a time (oi | exposure | volume), across the chart's
 * whole date range so the crosshair can scrub historic days.
 *
 * REST loads the active mode's endpoint for start..end; the exposure/volume
 * lanes then ride the greeks:{underlying}:1m SSE rows, upserted into the open
 * session's display-tf bucket (each bucket is latest-row-authoritative, so
 * replace-at-bucket self-heals after any missed update). OI bars are static
 * settled snapshots per session with no live channel. All REST prices arrive
 * already converted to ES points server-side; SSE rows carry native strikes
 * in spx_strike and are converted here (ratio = 1 for es).
 *
 * Everything lives in refs (no React state): the gutter canvas repaints from a
 * rAF fingerprint that reads versionRef, so SSE rows never re-render React.
 */

import { useEffect, useRef } from 'react';
import CONFIG from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from './useUnifiedSSE';
import { getLatestSessionDate, getOpenSessionDate, roundToTick, sessionDateOf } from '../utils/market';

export const COMPARE_UNDERLYINGS = ['es', 'spx', 'spy'];

// Backend-valid display timeframes (local copy; the GreeksHeatmapPlugin map
// is module-private, same pattern as UnderlyingQuoteIndicator/QuoteIVIndicator).
const TF_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1d': 86400,
};

const REFETCH_MS = 300000; // live-edge refresh cadence (matches the session-ratios effect)

// Globex session length in seconds (23h), mirror of the plugin's
// GLOBEX_DURATION: open + this = the session's Globex close.
const GLOBEX_DURATION_SEC = 82800;

function windowsTf(timeframe) {
  return (timeframe && TF_SECONDS[timeframe]) ? timeframe : '30m';
}

function emptyLane() {
  return { sessions: null, t1Sessions: null, oiSessions: null, ratios: null,
    tfSec: 1800, spot: null, zeroLevels: null, gammaLevels: null };
}

/** Mirror of GreeksHeatmapPlugin._mergeEthRows: ETH minutes all precede the
 *  stored RTH rows, so a plain concat stays time-sorted. */
function mergeEthRows(base, eth) {
  if (!eth || Object.keys(eth).length === 0) return base;
  const merged = { ...(base || {}) };
  for (const [dateKey, frag] of Object.entries(eth)) {
    const ethCandles = frag?.candles || [];
    if (!ethCandles.length) continue;
    const baseCandles = merged[dateKey]?.candles || [];
    merged[dateKey] = { ...(merged[dateKey] || {}), candles: ethCandles.concat(baseCandles) };
  }
  return merged;
}

/** 6-weekday walk-back over the session-ratios map (mirror of the chart's
 *  resolveAnchor); returns the T-1 anchor ratio for spx/spy or null. */
function walkBackRatio(ratioMap, day, underlying) {
  let key = day;
  for (let i = 0; i < 6 && key; i++) {
    const r = ratioMap?.[key];
    if (r && r[underlying] != null) return r[underlying];
    const d = new Date(key + 'T12:00:00');
    do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
    key = d.toISOString().split('T')[0];
  }
  return null;
}

/** Per-session anchor ratio for a lane (native points -> ES points).
 *  Anchors differ per session, so callers pass the SELECTED session's date. */
export function laneRatioFor(u, lane, dateKey, ratioMap) {
  if (u === 'es') return 1.0;
  const r = lane?.ratios?.[dateKey];
  if (r > 0) return r;
  const anchor = walkBackRatio(ratioMap, dateKey, u);
  return anchor > 0 ? anchor : null;
}

/**
 * Replica of GreeksHeatmapPlugin._selectTargetRow: newest row at-or-before t
 * across sessions; t == null (or nothing at-or-before) -> latest row overall;
 * when the cursor sits PAST the target row's window end and the open session
 * has rows, retarget to the open session so the current-day book stays
 * visible instead of falling back to the prior day's last row. Internal:
 * the exposure fill consumes it through selectExposureRow (a book persists
 * across sessions, so cross-session fallback is legitimate there).
 */
function selectRowAtTime(sessions, t, winSec) {
  if (!sessions) return { targetCandle: null, targetDateKey: null };
  let targetCandle = null;
  let targetDateKey = null;
  let latestCandle = null;
  let latestKey = null;
  for (const dateKey of Object.keys(sessions)) {
    const candles = sessions[dateKey]?.candles || [];
    for (let i = candles.length - 1; i >= 0; i--) {
      const c = candles[i];
      if (c.time == null) continue;
      if (!latestCandle || c.time > latestCandle.time) {
        latestCandle = c;
        latestKey = dateKey;
      }
      if (t != null && c.time <= t) {
        if (!targetCandle || c.time > targetCandle.time) {
          targetCandle = c;
          targetDateKey = dateKey;
        }
        break;
      }
    }
  }
  if (!targetCandle) {
    targetCandle = latestCandle;
    targetDateKey = latestKey;
  }
  const openDate = getOpenSessionDate();
  if (t != null && targetCandle && openDate
      && targetDateKey !== openDate && sessions[openDate]?.candles?.length) {
    if (t >= targetCandle.time + (winSec || 60)) {
      const oc = sessions[openDate].candles;
      let cand = null;
      for (let i = oc.length - 1; i >= 0; i--) {
        if (oc[i].time <= t) { cand = oc[i]; break; }
      }
      // No open-session row at-or-before the cursor (overnight there is
      // only the forming bucket): show the live forming row.
      targetCandle = cand || oc[oc.length - 1];
      targetDateKey = openDate;
    }
  }
  return { targetCandle, targetDateKey };
}

/**
 * Exposure row selection with the T+1 fallback: freshest wins, ties to the
 * real book. Sessions without a baked today-book (options holidays) have no
 * real rows, so the live T+1 series (next session's book evaluated during
 * the session) is hours-to-days fresher there and takes over, marked isT1.
 * On normal days the real book ties the T+1 series bucket-for-bucket and the
 * one-bucket threshold keeps a transient real-row stall from flickering the
 * display to a T+1 label.
 */
export function selectExposureRow(lane, t) {
  const normal = selectRowAtTime(lane?.sessions, t, lane?.tfSec);
  let t1Candle = null;
  let t1Key = null;
  const t1s = lane?.t1Sessions;
  if (t1s) {
    for (const dateKey of Object.keys(t1s)) {
      const candles = t1s[dateKey]?.candles || [];
      for (let i = candles.length - 1; i >= 0; i--) {
        const c = candles[i];
        if (c.time == null) continue;
        if (t != null && c.time > t) continue;
        if (!t1Candle || c.time > t1Candle.time) { t1Candle = c; t1Key = dateKey; }
        break;
      }
    }
  }
  const winSec = lane?.tfSec || 1800;
  if (t1Candle && (!normal.targetCandle || t1Candle.time > normal.targetCandle.time + winSec)) {
    return { bars: t1Candle.levels || null, dateKey: t1Key, isT1: true, t1Key };
  }
  return {
    bars: normal.targetCandle?.levels || null,
    dateKey: normal.targetDateKey,
    isT1: false,
    t1Key: null,
  };
}

/**
 * Volume row under the cursor: volume is per-session measured flow, so the
 * selection NEVER crosses session boundaries (a lane with no rows for the
 * cursor's session honestly shows "no data", not the prior session's
 * totals). t == null resolves to the open session, or the just-closed one
 * during the maintenance gap / weekend.
 */
export function selectVolumeRow(lane, t) {
  const sessions = lane?.sessions;
  if (!sessions) return { bars: null, dateKey: null };
  let target;
  if (t != null) {
    target = sessionDateOf(t);
  } else {
    target = getOpenSessionDate();
    if (!target) {
      const keys = Object.keys(sessions).sort();
      target = keys.length ? keys[keys.length - 1] : null;
    }
  }
  if (!target) return { bars: null, dateKey: null };
  const candles = sessions[target]?.candles || [];
  let row = null;
  for (let i = candles.length - 1; i >= 0; i--) {
    const c = candles[i];
    if (c.time == null) continue;
    if (t == null || c.time <= t) { row = c; break; }
  }
  return { bars: row?.levels || null, dateKey: target };
}

/**
 * Session snapshot under the cursor for the OI fill. t == null resolves to
 * NOW, so the rest view and hovering the live candle agree by construction.
 * Picks the max session whose Globex open (epoch seconds) is at-or-before t;
 * when that session's Globex day has ENDED and a later snapshot exists,
 * promotes to it (mirror of the plugin's future-session promotion into the
 * developing slot): covers options holidays (no snapshot for the holiday
 * session), the 17:00-18:00 ET maintenance gap, and weekends.
 */
export function selectOiSession(oiSessions, t) {
  if (!oiSessions) return { dateKey: null, promoted: false };
  const keys = Object.keys(oiSessions).sort();
  if (!keys.length) return { dateKey: null, promoted: false };
  const at = t != null ? t : Date.now() / 1000;
  let best = null;
  for (const dateKey of keys) {
    const open = oiSessions[dateKey]?.globex_open_ts;
    if (open != null && open <= at
        && (best == null || open > oiSessions[best].globex_open_ts)) {
      best = dateKey;
    }
  }
  if (best == null) {
    // Cursor before every loaded session (or only forward sessions loaded):
    // the earliest entry is the closest one.
    return { dateKey: keys[0], promoted: false };
  }
  if (at >= oiSessions[best].globex_open_ts + GLOBEX_DURATION_SEC) {
    let next = null;
    for (const dateKey of keys) {
      const open = oiSessions[dateKey]?.globex_open_ts;
      if (open != null && open > oiSessions[best].globex_open_ts
          && (next == null || open < oiSessions[next].globex_open_ts)) {
        next = dateKey;
      }
    }
    if (next != null) return { dateKey: next, promoted: true };
  }
  return { dateKey: best, promoted: false };
}

export function useUnderlyingCompareData({
  mode, expiryMode, timeframe, date, endDate, sessionRatiosRef, wantZero, wantGamma,
}) {
  const lanesRef = useRef({ es: emptyLane(), spx: emptyLane(), spy: emptyLane() });
  const versionRef = useRef(0);
  const modeRef = useRef(mode);
  const expiryModeRef = useRef(expiryMode);
  const endDateRef = useRef(endDate);
  modeRef.current = mode;
  expiryModeRef.current = expiryMode;
  endDateRef.current = endDate;

  const bump = () => { versionRef.current += 1; };

  // Canonical ratio precedence for SSE rows, mirroring _resolveRowRatio:
  // REST session anchor first (levels stay static all session), then the
  // row's own basis, then the SSE live-ratio hint, then the walk-back.
  const resolveSseRatio = (u, dateStr, row, message) => {
    if (u === 'es') return 1.0;
    const lane = lanesRef.current[u];
    if (lane.ratios?.[dateStr] > 0) return lane.ratios[dateStr];
    if (row && row.ratio > 0) return row.ratio;
    if (row && row.spot_es > 0 && row.spot_spx > 0) return row.spot_es / row.spot_spx;
    if (message && message.es_spx_ratio > 0) return message.es_spx_ratio;
    const anchor = walkBackRatio(sessionRatiosRef?.current, dateStr, u);
    return anchor > 0 ? anchor : null;
  };

  // REST: active mode's endpoint over the chart's date range, 3 underlyings
  // in parallel. replace=true swaps the whole store (range/mode change);
  // replace=false merges per-date (the live-edge refresh of endDate only).
  useEffect(() => {
    let cancelled = false;
    let reqSeq = 0;
    const tfFallbackSec = TF_SECONDS[windowsTf(timeframe)];

    const fetchSpan = async (startD, endD, replace) => {
      const seq = ++reqSeq;
      const em = expiryModeRef.current || '0dte';
      const tf = windowsTf(timeframe);
      const base = `${CONFIG.apiUrl}/api/greeks`;
      const urlFor = (u) => {
        if (mode === 'exposure') {
          return `${base}/exposure-rows?start_date=${startD}&end_date=${endD}&expiry_mode=${em}&underlying=${u}&tf=${tf}&rv=9`;
        }
        if (mode === 'volume') {
          return `${base}/volume-rows?start_date=${startD}&end_date=${endD}&expiry_mode=${em}&underlying=${u}&tf=${tf}&rv=3`;
        }
        return `${base}/oi-bars?start_date=${startD}&end_date=${endD}&expiry_mode=${em}&underlying=${u}&rv=1`;
      };

      const results = await Promise.allSettled(COMPARE_UNDERLYINGS.map(async (u) => {
        const resp = await authFetch(urlFor(u));
        if (!resp.ok) throw new Error(`gutter ${mode} ${u}: ${resp.status}`);
        return { u, data: await resp.json() };
      }));
      if (cancelled || seq !== reqSeq) return;

      for (const res of results) {
        if (res.status !== 'fulfilled') continue;
        const { u, data } = res.value;
        const lane = lanesRef.current[u];
        if (mode === 'oi') {
          const incoming = data.oi_bars || {};
          lane.oiSessions = replace ? incoming : { ...(lane.oiSessions || {}), ...incoming };
        } else {
          const incoming = (mode === 'exposure')
            ? (mergeEthRows(data.exposure_rows || null, data.eth_rows) || {})
            : Object.fromEntries(Object.entries(data.volume_rows || {})
              .filter(([, candles]) => Array.isArray(candles) && candles.length)
              .map(([d, candles]) => [d, { candles }]));
          lane.sessions = replace ? incoming : { ...(lane.sessions || {}), ...incoming };
          if (mode === 'exposure') {
            const t1Incoming = mergeEthRows(data.t1_rows || null, data.eth_t1_rows) || {};
            lane.t1Sessions = replace ? t1Incoming : { ...(lane.t1Sessions || {}), ...t1Incoming };
          }
        }
        // All three modes serve the rule-7 meta block (exposure-rows
        // since 3.5; volume-rows/oi-bars since 5.9). oi-bars meta has no
        // tf, so the oi lane always takes tfFallbackSec.
        const metaBlock = data.meta;
        lane.tfSec = TF_SECONDS[metaBlock?.tf] || tfFallbackSec;
        if (u !== 'es' && metaBlock?.ratios_by_date) {
          lane.ratios = { ...(lane.ratios || {}), ...metaBlock.ratios_by_date };
        }
      }
      bump();
    };

    // Range/mode change: drop the old mode's containers so stale bars can
    // never render while the new fetch is in flight, then load the range.
    for (const u of COMPARE_UNDERLYINGS) {
      lanesRef.current[u].sessions = null;
      lanesRef.current[u].t1Sessions = null;
      lanesRef.current[u].oiSessions = null;
    }
    bump();
    fetchSpan(date || endDate, endDate, true);

    // Live-edge refresh: history is immutable, so only the latest session is
    // re-pulled, and only while the range actually ends at the live session.
    const interval = setInterval(() => {
      if (endDate !== getLatestSessionDate()) return;
      fetchSpan(endDate, endDate, false);
    }, REFETCH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [mode, expiryMode, timeframe, date, endDate]);

  // Zero/Gamma Levels per lane: per-day baked, ES-point prices, tiny
  // payloads. Own effect so a level toggle never churns the fill fetch.
  // Both routes are rate-limited (5/10s per IP): stagger the three
  // per-underlying calls so a burst never lands alongside the main-chart
  // level poll. A failed/429 call keeps prior data; the next cycle heals.
  useEffect(() => {
    if (!wantZero && !wantGamma) return undefined;
    let cancelled = false;
    const pull = async (u, kind, field, startD, endD, replace) => {
      try {
        const em = expiryModeRef.current || '0dte';
        const url = `${CONFIG.apiUrl}/api/indicators/${kind}?start_date=${startD}&end_date=${endD}&expiry_mode=${em}&underlying=${u}`;
        const resp = await authFetch(url);
        if (!resp.ok || cancelled) return;
        const incoming = (await resp.json()).levels_by_day || {};
        const lane = lanesRef.current[u];
        lane[field] = replace ? incoming : { ...(lane[field] || {}), ...incoming };
        bump();
      } catch { /* network: keep prior data */ }
    };
    const fetchAll = (startD, endD, replace) => {
      COMPARE_UNDERLYINGS.forEach((u, i) => {
        setTimeout(() => {
          if (cancelled) return;
          if (wantZero) pull(u, 'zero-levels', 'zeroLevels', startD, endD, replace);
          if (wantGamma) pull(u, 'gamma-levels', 'gammaLevels', startD, endD, replace);
        }, i * 150);
      });
    };
    for (const u of COMPARE_UNDERLYINGS) {
      lanesRef.current[u].zeroLevels = null;
      lanesRef.current[u].gammaLevels = null;
    }
    bump();
    fetchAll(date || endDate, endDate, true);
    const interval = setInterval(() => {
      if (endDate !== getLatestSessionDate()) return;
      fetchAll(endDate, endDate, false);
    }, REFETCH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [wantZero, wantGamma, expiryMode, date, endDate]);

  // SSE: live per-strike rows for exposure/volume; live spx/spy spot always.
  useEffect(() => {
    const unsubs = [];

    // Shared forming-row ingest for the today and T+1 series: convert native
    // strikes and upsert at the display-tf bucket (mirror of the plugin's
    // _upsertLiveExposureRow/_upsertLiveVolumeRow).
    const ingestSseRow = (u, message, containerField) => {
      const row = message.data;
      if (!row || !Array.isArray(row.levels) || row.time == null) return;
      const lane = lanesRef.current[u];
      const dateStr = row.date || getOpenSessionDate() || endDateRef.current;
      const ratio = resolveSseRatio(u, dateStr, row, message);
      if (!ratio) return; // cannot place the row in ES space yet
      const esLevels = [];
      for (const lv of row.levels) {
        if (lv.spx_strike == null) continue;
        const price = roundToTick(lv.spx_strike * ratio);
        esLevels.push(mode === 'exposure'
          ? { price, gex: lv.gex || 0, dex: lv.dex || 0, vex: lv.vex || 0, cex: lv.cex || 0 }
          : { price, call_vol: lv.call_vol || 0, put_vol: lv.put_vol || 0 });
      }
      if (!esLevels.length) return;
      const winSec = lane.tfSec || 1800;
      const bucketTime = Math.floor(row.time / winSec) * winSec;
      if (!lane[containerField]) lane[containerField] = {};
      if (!lane[containerField][dateStr]) lane[containerField][dateStr] = { candles: [] };
      const candles = lane[containerField][dateStr].candles;
      const idx = candles.findIndex((c) => c.time === bucketTime);
      if (idx >= 0) {
        candles[idx] = { time: bucketTime, levels: esLevels };
      } else {
        candles.push({ time: bucketTime, levels: esLevels });
        candles.sort((a, b) => a.time - b.time);
      }
      // Seed the session anchor only when absent (overnight before any
      // REST metadata), mirroring _applyMessageMetadata.
      if (u !== 'es' && !(lane.ratios?.[dateStr] > 0)) {
        lane.ratios = { ...(lane.ratios || {}), [dateStr]: ratio };
      }
      bump();
    };

    if (mode === 'exposure' || mode === 'volume') {
      // Exposure also rides the T+1 event so no-book sessions (options
      // holidays) stay live through the freshest-wins fallback.
      const events = mode === 'exposure'
        ? [['greeks_exposure_row', 'sessions'], ['greeks_exposure_row_t1', 't1Sessions']]
        : [['greeks_volume_row', 'sessions']];
      for (const u of COMPARE_UNDERLYINGS) {
        for (const [eventName, containerField] of events) {
          unsubs.push(unifiedSSE.subscribe(`greeks:${u}:1m`, eventName, (message) => {
            try {
              if (modeRef.current !== mode) return; // late message after a fill switch
              if (message.mode && message.mode !== (expiryModeRef.current || '0dte')) return;
              if (message.underlying && message.underlying !== u) return;
              ingestSseRow(u, message, containerField);
            } catch (e) {
              console.error('[UnderlyingCompare] SSE row error:', e);
            }
          }));
        }
      }
    }

    // Spot ticks: the underlying-quotes channel has no 'es' entry; the ES
    // lane's spot comes from the chart's own candle series in the component.
    unsubs.push(unifiedSSE.subscribe('underlying-quotes', 'underlying_quote', (data) => {
      try {
        const u = data.underlying;
        if ((u !== 'spx' && u !== 'spy') || !data.bar || data.bar.c == null) return;
        lanesRef.current[u].spot = data.bar.c;
        bump();
      } catch (e) {
        console.error('[UnderlyingCompare] quote error:', e);
      }
    }));

    return () => { unsubs.forEach((fn) => fn()); };
  }, [mode, expiryMode]);

  return { lanesRef, versionRef };
}
